/**
 * Workshop floor plan. One place owns where everything lives so the Director
 * (who sends characters places) and the scene components (who draw furniture)
 * can never disagree.
 *
 * Layout (12×9 tiles, door on the upper-left wall, camera from the south):
 *
 *                        (0,0)
 *              console ◤      ◥ bookshelf
 *             cabinet ◤          ◥
 *             window ◤             ◥ workbench
 *              inbox ◤     desk      ◥ plant
 *               door ◤    (rug)        ◥ bench-0
 *                    ◤                  ◥ bench-1
 *                    ◤   delivery        ◥ bench-2
 *                 (0,9)      ◥        ◤ bench-3
 *                              (12,9)
 *
 * `standAt` is where the character's feet plant; wall furniture is approached
 * from below (character in front), desks occlude the character's lower body
 * (character one cell above the desk footprint — painter's depth does the rest).
 */

import { blockCells, cellCenter, isWalkable, project } from './iso'
import type { GridPoint, StationId } from './types'

export interface Station {
  id: StationId
  /** Character anchor (continuous grid coords). */
  standAt: GridPoint
  /** Screen-space facing while performing here (legacy 2D hint). */
  facing: 1 | -1
  /**
   * World yaw while performing (three.js convention: 0 faces +z ≡ grid +y,
   * π/2 faces +x). Wall stations face the wall; desks face the camera side.
   */
  yaw: number
  /** Furniture origin for the scene component (continuous grid coords). */
  at: GridPoint
}

const FACE_NEG_X = -Math.PI / 2
const FACE_NEG_Z = Math.PI
const FACE_POS_Z = 0

/**
 * Centre of the standup circle — the open rug in front of the lead's desk.
 * The ring of spots around it (radius HUDDLE_R) lands entirely on walkable
 * cells [4..6, 2..4], so nobody ever huddles inside the furniture.
 */
export const HUDDLE_CENTER: GridPoint = { x: 5.6, y: 3.35 }
const HUDDLE_R = 0.95

export const STATIONS: Record<StationId, Station> = {
  door: { id: 'door', standAt: cellCenter(1, 6), facing: -1, yaw: FACE_NEG_X, at: { x: 0, y: 6.5 } },
  // Waiting spot: in front of the back-wall window, gazing outside.
  window: { id: 'window', standAt: { x: 1.75, y: 0.85 }, facing: -1, yaw: FACE_NEG_Z, at: { x: 1.75, y: 0 } },
  inbox: { id: 'inbox', standAt: cellCenter(1, 4), facing: -1, yaw: FACE_NEG_X, at: { x: 0.45, y: 4.5 } },
  cabinet: { id: 'cabinet', standAt: cellCenter(1, 2), facing: -1, yaw: FACE_NEG_X, at: { x: 0.42, y: 2.5 } },
  console: { id: 'console', standAt: cellCenter(1, 0), facing: -1, yaw: FACE_NEG_X, at: { x: 0.42, y: 0.6 } },
  shelf: { id: 'shelf', standAt: cellCenter(4, 1), facing: 1, yaw: FACE_NEG_Z, at: { x: 4.5, y: 0.42 } },
  workbench: { id: 'workbench', standAt: cellCenter(8, 1), facing: 1, yaw: FACE_NEG_Z, at: { x: 8.5, y: 0.45 } },
  // Pallet immediately to the left of the bench — every crate built on the
  // bench is carried the one tile over and stacked here by hand.
  crates: { id: 'crates', standAt: cellCenter(7, 1), facing: 1, yaw: FACE_NEG_Z, at: { x: 7.18, y: 0.62 } },
  desk: { id: 'desk', standAt: cellCenter(5, 4), facing: 1, yaw: FACE_POS_Z, at: { x: 5.95, y: 5.5 } },
  // Kitchenette counter below the door — the pause-turn coffee break.
  coffee: { id: 'coffee', standAt: cellCenter(1, 8), facing: -1, yaw: FACE_NEG_X, at: { x: 0.45, y: 8.4 } },
  // Left-wall planning board and the water cooler by the door: the two spots
  // a character drifts to when there is a lull but the run is still going.
  whiteboard: { id: 'whiteboard', standAt: { x: 1.45, y: 3.9 }, facing: -1, yaw: FACE_NEG_X, at: { x: 0.1, y: 3.9 } },
  cooler: { id: 'cooler', standAt: { x: 1.5, y: 5.5 }, facing: -1, yaw: FACE_NEG_X, at: { x: 0.5, y: 5.55 } },
  // Virtual: the standup circle on the rug. Real spots come from huddleSpot().
  huddle: { id: 'huddle', standAt: { ...HUDDLE_CENTER }, facing: 1, yaw: FACE_NEG_Z, at: { ...HUDDLE_CENTER } },
  delivery: { id: 'delivery', standAt: cellCenter(3, 6), facing: -1, yaw: FACE_POS_Z, at: { x: 3.4, y: 7.5 } },
  'bench-0': { id: 'bench-0', standAt: cellCenter(10, 1), facing: -1, yaw: FACE_POS_Z, at: { x: 10.5, y: 2.5 } },
  'bench-1': { id: 'bench-1', standAt: cellCenter(10, 3), facing: -1, yaw: FACE_POS_Z, at: { x: 10.5, y: 4.5 } },
  'bench-2': { id: 'bench-2', standAt: cellCenter(10, 5), facing: -1, yaw: FACE_POS_Z, at: { x: 10.5, y: 6.5 } },
  'bench-3': { id: 'bench-3', standAt: cellCenter(10, 7), facing: -1, yaw: FACE_POS_Z, at: { x: 10.5, y: 8.5 } }
}

/** Where the main character stands when briefing a bench specialist. */
export function benchBriefSpot(benchIndex: number): GridPoint {
  const clamped = Math.min(3, Math.max(0, benchIndex))
  return cellCenter(9, 1 + clamped * 2)
}

export const BENCH_STATIONS: StationId[] = ['bench-0', 'bench-1', 'bench-2', 'bench-3']

/** Spawn point just outside the door (walk-in entrance). */
export const DOOR_SPAWN: GridPoint = { x: -0.9, y: 6.5 }

/**
 * Staggered outside spots so simultaneously arriving/leaving specialists queue
 * up on the doorstep instead of standing inside each other.
 */
export function doorSpawnFor(slot: number): GridPoint {
  const k = Math.max(0, slot)
  return { x: DOOR_SPAWN.x - (k % 2) * 0.55 - Math.floor(k / 2) * 0.15, y: DOOR_SPAWN.y + (k % 3) * 0.45 - 0.25 }
}

/** True when a point is close enough to the doorway that the door should swing. */
export function inDoorZone(p: GridPoint): boolean {
  return p.x < 1.7 && p.y > 5.2 && p.y < 8.2
}

/**
 * Shared-station queue spots: where the Nth simultaneous visitor stands.
 * The bookshelf is three tiles wide, so readers fan out along it; the
 * workbench takes a second worker shoulder to shoulder at the far end; other
 * stations get a generic nudge.
 */
export function stationSlot(station: StationId, slot: number): GridPoint {
  const base = STATIONS[station].standAt
  if (slot <= 0) return base
  if (station === 'shelf') {
    const dx = [0, 0.85, -0.85, 1.55][Math.min(slot, 3)] ?? 0
    return { x: base.x + dx, y: base.y }
  }
  if (station === 'workbench') {
    // The bench spans x 8..9: the second pair of hands works its right end.
    return { x: base.x + 1, y: base.y }
  }
  // Generic nudge: alternate sides, half a tile apart.
  const side = slot % 2 === 1 ? 1 : -1
  const spread = Math.ceil(slot / 2) * 0.55
  return { x: base.x + side * spread * 0.4, y: base.y + side * spread * 0.6 }
}

/** How many people can work the bench at once before it reads as a scrum. */
export const WORKBENCH_SLOTS = 2

/**
 * Crates the pallet beside the bench holds (2 wide × 3 high — any taller and
 * the stack climbs into the wall sign from the iso camera). Shared by the
 * Director, which stops stacking here and starts walking the overflow
 * straight to the packing table, and by the scene that lays the stack out.
 */
export const CRATE_CAPACITY = 6

/**
 * Where the Nth of `total` participants stands in the standup circle, and
 * which way they face (inward — a circle of people looking at each other is
 * the whole point). Slot 0 is the lead, at the desk side of the ring.
 */
export function huddleSpot(index: number, total: number): GridPoint {
  const n = Math.max(1, total)
  const a = (index / n) * Math.PI * 2 + Math.PI * 0.35
  return { x: HUDDLE_CENTER.x + Math.sin(a) * HUDDLE_R, y: HUDDLE_CENTER.y + Math.cos(a) * HUDDLE_R }
}

/** Screen-space facing + world yaw for a character at `from` looking at `to`. */
export function faceToward(from: GridPoint, to: GridPoint): { facing: 1 | -1; yaw: number } {
  const dx = to.x - from.x
  const dy = to.y - from.y
  const a = project(from)
  const b = project(to)
  const facing: 1 | -1 = b.x - a.x >= 0 ? 1 : -1
  if (Math.abs(dx) < 1e-4 && Math.abs(dy) < 1e-4) return { facing, yaw: 0 }
  return { facing, yaw: Math.atan2(dx, dy) }
}

/**
 * A walkable spot next to `target` — where you'd stand to talk to someone or
 * hand them something. Prefers the side the visitor is already coming from so
 * they don't loop around the desk.
 */
export function besideSpot(target: GridPoint, from: GridPoint): GridPoint {
  const cx = Math.floor(target.x)
  const cy = Math.floor(target.y)
  const candidates: Array<[number, number]> = [
    [cx + 1, cy],
    [cx - 1, cy],
    [cx, cy + 1],
    [cx, cy - 1],
    [cx + 1, cy + 1],
    [cx - 1, cy - 1]
  ]
  let best: GridPoint | null = null
  let bestD = Number.POSITIVE_INFINITY
  for (const [x, y] of candidates) {
    if (!isWalkable(x, y)) continue
    const p = cellCenter(x, y)
    const d = Math.hypot(p.x - from.x, p.y - from.y)
    if (d < bestD) {
      bestD = d
      best = p
    }
  }
  return best ?? { ...target }
}

/** Furniture footprints — cells characters may never cross. */
export function installFootprints(): void {
  blockCells([
    // Upper-left wall line (console, cabinet, inbox occupy the x=0 column,
    // except the door opening at y=6).
    [0, 0],
    [0, 1],
    [0, 2],
    [0, 3],
    [0, 4],
    [0, 5],
    [0, 8],
    // Upper-right wall furniture (bookshelf spans 3..6, workbench 8..9).
    [3, 0],
    [4, 0],
    [5, 0],
    [6, 0],
    // Crate pallet wedged between the bookshelf and the bench — walkers go
    // around it, which is why the "carry it over and stack it" trip reads.
    [7, 0],
    [8, 0],
    [9, 0],
    // Plant corner.
    [11, 0],
    // Main desk (two tiles wide, character sits behind at (5,4)).
    [5, 5],
    [6, 5],
    // Delivery table.
    [2, 7],
    [3, 7],
    // Specialist bench desks (characters sit behind at (10, even rows)).
    [10, 2],
    [10, 4],
    [10, 6],
    [10, 8],
    // Glass privacy fins beside each bench desk (x≈9.6) — walkers pass through
    // the gaps at the odd rows, never through the glass.
    [9, 2],
    [9, 4],
    [9, 6],
    [9, 8]
  ])
}

installFootprints()
